/**
 * sketchFrame.js — shared, dependency-free performance helpers for the art site.
 *
 * These utilities keep dozens of animated canvases on a single page cheap: they
 * stop work whenever an element is off-screen or the tab is backgrounded, and
 * coalesce noisy pointer/scroll handlers to one call per animation frame.
 *
 *   - whenVisible        generic on-screen + tab-visible gate (for any rAF loop)
 *   - pauseWhenHidden    p5-specific convenience built on whenVisible
 *   - rafThrottle        coalesce rapid handlers to one call per frame
 *   - prefersReducedMotion  OS reduced-motion boolean
 *
 * Pure ES module, no external dependencies, fully defensive (safe to call with
 * null/undefined args or in non-browser/SSR environments).
 */

/**
 * Generic visibility gate. Calls onEnter() when `element` is on-screen AND the
 * tab is visible, and onLeave() when it scrolls away OR the tab is hidden.
 *
 * This is the primitive that non-p5 components (MusicBumper, StaticFM,
 * DeadChannel) use to start/stop their own requestAnimationFrame loops, and
 * which pauseWhenHidden is implemented on top of.
 *
 * If IntersectionObserver is unavailable (older browsers / SSR) it degrades to
 * calling onEnter() exactly once so the component still runs.
 *
 * @param {Element}  element  The DOM element to observe.
 * @param {Function} onEnter  Called when visible + tab focused.
 * @param {Function} onLeave  Called when off-screen or tab hidden.
 * @param {object}  [opts]
 * @param {number}  [opts.threshold=0.05]  IntersectionObserver threshold.
 * @param {string}  [opts.rootMargin]      Optional IntersectionObserver rootMargin.
 * @returns {() => void}  Cleanup: disconnects the observer and removes the
 *                        visibilitychange listener. Always safe to call.
 */
export function whenVisible(element, onEnter, onLeave, opts = {}) {
  const noop = () => {};
  // Normalize callbacks so we never call a non-function.
  const enter = typeof onEnter === 'function' ? onEnter : noop;
  const leave = typeof onLeave === 'function' ? onLeave : noop;

  // No element to watch → nothing to do.
  if (!element) return noop;

  // No IntersectionObserver (SSR / very old browsers): we cannot detect
  // scrolling, so just enter once and let the component run.
  if (typeof IntersectionObserver === 'undefined') {
    enter();
    return noop;
  }

  // Track the two independent reasons to be active: on-screen AND tab visible.
  let onScreen = false;
  const doc = typeof document !== 'undefined' ? document : null;
  let tabVisible = doc ? !doc.hidden : true;

  // Remember the last applied state so we only fire on real transitions
  // (avoids redundant enter/leave calls when one input changes but the
  // combined result does not).
  let active = false;
  const apply = () => {
    const next = onScreen && tabVisible;
    if (next === active) return;
    active = next;
    if (next) enter();
    else leave();
  };

  // --- IntersectionObserver: track whether the element is on-screen. ---
  const threshold = typeof opts.threshold === 'number' ? opts.threshold : 0.05;
  const ioOptions = { threshold };
  if (opts.rootMargin) ioOptions.rootMargin = opts.rootMargin;

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      onScreen = entry.isIntersecting;
    }
    apply();
  }, ioOptions);
  observer.observe(element);

  // --- document visibilitychange: track whether the tab is foregrounded. ---
  let onVisibilityChange = null;
  if (doc && typeof doc.addEventListener === 'function') {
    onVisibilityChange = () => {
      tabVisible = !doc.hidden;
      apply();
    };
    doc.addEventListener('visibilitychange', onVisibilityChange);
  }

  // Cleanup: detach everything. Guards keep it safe even if a piece was never
  // attached.
  return () => {
    observer.disconnect();
    if (doc && onVisibilityChange) {
      doc.removeEventListener('visibilitychange', onVisibilityChange);
      onVisibilityChange = null;
    }
  };
}

/**
 * Pause a p5 instance's draw loop when its element is off-screen OR the tab is
 * hidden, and resume it when the element returns AND the tab is visible.
 *
 * Thin p5-aware wrapper around whenVisible(): on enter it calls loop() then
 * redraw() (if present) so a paused sketch repaints immediately rather than
 * waiting a frame; on leave it calls noLoop().
 *
 * @param {object}  p5Instance  A p5 instance (expects .loop()/.noLoop(), optional .redraw()).
 * @param {Element} element     The DOM element wrapping the canvas (the ref div).
 * @param {object} [opts]
 * @param {number} [opts.threshold=0.05]  IntersectionObserver visibility threshold.
 * @param {string} [opts.rootMargin]      Optional IntersectionObserver rootMargin.
 * @returns {() => void}  Cleanup function (same contract as whenVisible).
 */
export function pauseWhenHidden(p5Instance, element, opts = {}) {
  const noop = () => {};

  // Bail out if the essentials are missing or the p5 API is incomplete.
  if (!p5Instance || !element) return noop;
  if (typeof p5Instance.loop !== 'function' || typeof p5Instance.noLoop !== 'function') {
    return noop;
  }

  return whenVisible(
    element,
    () => {
      p5Instance.loop();
      if (typeof p5Instance.redraw === 'function') p5Instance.redraw();
    },
    () => {
      p5Instance.noLoop();
    },
    opts
  );
}

/**
 * Wrap a handler so it runs at most once per animation frame, using the latest
 * arguments. Ideal for cheap mousemove/scroll handlers that would otherwise fire
 * far more often than the display refreshes.
 *
 * @param {Function} fn  The handler to throttle.
 * @returns {Function}   A throttled wrapper. The wrapper carries a `.cancel()`
 *                       method to drop any pending scheduled call.
 */
export function rafThrottle(fn) {
  if (typeof fn !== 'function') return () => {};

  // Fall back to a timer where rAF is unavailable (e.g. SSR).
  const raf =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (cb) => setTimeout(cb, 16);
  const caf =
    typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame : clearTimeout;

  let scheduled = false;
  let lastArgs = null;
  let lastThis = null;
  let handle = null;

  const throttled = function throttled(...args) {
    // Always remember the most recent call so the frame uses fresh data.
    lastArgs = args;
    lastThis = this;
    if (scheduled) return;
    scheduled = true;
    handle = raf(() => {
      scheduled = false;
      handle = null;
      fn.apply(lastThis, lastArgs);
      lastArgs = null;
      lastThis = null;
    });
  };

  // Allow callers to drop a pending frame (e.g. on unmount).
  throttled.cancel = () => {
    if (scheduled && handle != null) caf(handle);
    scheduled = false;
    handle = null;
    lastArgs = null;
    lastThis = null;
  };

  return throttled;
}

/**
 * @returns {boolean} true if the user has requested reduced motion via the OS,
 *                    false otherwise (and false where matchMedia is unavailable).
 */
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
